import { formatDate, formatDateFull, formatInteger, formatMonth, formatPercent, formatValue, isNumber } from './piFormat.js';
import { immatureText } from './piState.js';

export const SEQUENTIAL_COLORS = ['#f2f6fe', '#dbe6fd', '#b9cefb', '#8fb0f9', '#5f8cf7', '#2f66f3', '#1a45b8'];

const DARK_TEXT = '#1f2933';
const LIGHT_TEXT = '#ffffff';
const FIRST_LIGHT_TEXT_STEP = 5;

export const SERIES_COLORS = ['#1b59f8', '#12a150', '#b35c00', '#8e44ad', '#c0392b', '#16a085'];

export const SERIES_DASHES = ['', '6 4', '2 3', '10 3 2 3', '1 4', '8 2'];

export const sequentialStep = (pct) => {
  if (!isNumber(pct)) return -1;
  const clamped = Math.min(100, Math.max(0, pct));
  const last = SEQUENTIAL_COLORS.length - 1;
  return Math.min(last, Math.floor((clamped / 100) * SEQUENTIAL_COLORS.length));
};

export const sequentialStyle = (pct) => {
  const step = sequentialStep(pct);
  if (step < 0) return { step: -1, background: '#ffffff', color: DARK_TEXT };
  return {
    step: step,
    background: SEQUENTIAL_COLORS[step],
    color: step >= FIRST_LIGHT_TEXT_STEP ? LIGHT_TEXT : DARK_TEXT,
  };
};

export const scaleLegend = () => {
  const size = 100 / SEQUENTIAL_COLORS.length;
  return SEQUENTIAL_COLORS.map((background, step) => {
    const from = Math.round(step * size);
    const to = step === SEQUENTIAL_COLORS.length - 1 ? 100 : Math.round((step + 1) * size);
    return {
      step: step,
      background: background,
      color: step >= FIRST_LIGHT_TEXT_STEP ? LIGHT_TEXT : DARK_TEXT,
      label: from + '–' + to + '%',
    };
  });
};

export const extent = (values) => {
  let min = null;
  let max = null;
  (values || []).forEach((value) => {
    if (!isNumber(value)) return;
    if (min === null || value < min) min = value;
    if (max === null || value > max) max = value;
  });
  return { min: min, max: max };
};

export const niceStep = (range, count) => {
  if (!isNumber(range) || range <= 0) return 1;
  const raw = range / Math.max(1, count || 5);
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const residual = raw / magnitude;
  let nice = 10;
  if (residual <= 1) nice = 1;
  else if (residual <= 2) nice = 2;
  else if (residual <= 2.5) nice = 2.5;
  else if (residual <= 5) nice = 5;
  return nice * magnitude;
};

const roundTick = (value) => Math.round(value * 1e6) / 1e6;

export const niceTicks = (min, max, count) => {
  const low = isNumber(min) ? Math.min(0, min) : 0;
  const high = isNumber(max) && max > low ? max : low + 1;
  const step = niceStep(high - low, count || 4);
  const start = Math.floor(low / step) * step;
  const end = Math.ceil(high / step) * step;
  const ticks = [];
  for (let value = start; value <= end + step / 2; value += step) ticks.push(roundTick(value));
  return ticks;
};

export const linearScale = (domainMin, domainMax, rangeMin, rangeMax) => {
  const span = domainMax - domainMin;
  return (value) => {
    if (!span) return (rangeMin + rangeMax) / 2;
    return rangeMin + ((value - domainMin) / span) * (rangeMax - rangeMin);
  };
};

const coordinate = (value) => String(Math.round(value * 10) / 10);

export const linePath = (points) => {
  let path = '';
  let drawing = false;
  (points || []).forEach((point) => {
    if (!point || !isNumber(point.x) || !isNumber(point.y)) {
      drawing = false;
      return;
    }
    path += (drawing ? 'L' : 'M') + coordinate(point.x) + ',' + coordinate(point.y);
    drawing = true;
  });
  return path;
};

const segmentsOf = (points) => {
  const segments = [];
  let current = [];
  (points || []).forEach((point) => {
    if (!point || !isNumber(point.x) || !isNumber(point.y)) {
      if (current.length) segments.push(current);
      current = [];
      return;
    }
    current.push(point);
  });
  if (current.length) segments.push(current);
  return segments;
};

export const areaPath = (points, baselineY) => segmentsOf(points)
  .map((segment) => {
    const first = segment[0];
    const last = segment[segment.length - 1];
    return linePath(segment) +
      'L' + coordinate(last.x) + ',' + coordinate(baselineY) +
      'L' + coordinate(first.x) + ',' + coordinate(baselineY) + 'Z';
  })
  .join('');

const labelIndexes = (count, wanted) => {
  if (count <= 0) return [];
  if (count <= wanted) return Array.from({ length: count }, (_, i) => i);
  const indexes = [];
  const step = (count - 1) / (wanted - 1);
  for (let i = 0; i < wanted; i++) {
    const index = Math.round(i * step);
    if (indexes.indexOf(index) === -1) indexes.push(index);
  }
  return indexes;
};

export const buildLineChart = (series, options) => {
  const settings = options || {};
  const width = settings.width || 900;
  const height = settings.height || 240;
  const padding = Object.assign({ top: 16, right: 16, bottom: 28, left: 56 }, settings.padding || {});
  const unit = settings.unit || 'count';
  const list = (series || []).filter((item) => item && Array.isArray(item.points));
  const dates = [];
  list.forEach((item) => {
    item.points.forEach((point) => {
      if (point && point.date && dates.indexOf(point.date) === -1) dates.push(point.date);
    });
  });
  dates.sort();
  const values = [];
  list.forEach((item) => item.points.forEach((point) => {
    if (point && isNumber(point.value)) values.push(point.value);
  }));
  const range = extent(values);
  const empty = !dates.length || range.max === null;
  const ticks = empty ? [0, 1] : niceTicks(range.min, range.max, settings.tickCount || 4);
  const yMin = ticks[0];
  const yMax = ticks[ticks.length - 1];
  const plotLeft = padding.left;
  const plotRight = width - padding.right;
  const plotTop = padding.top;
  const plotBottom = height - padding.bottom;
  const x = linearScale(0, Math.max(1, dates.length - 1), plotLeft, plotRight);
  const xOf = (date) => (dates.length === 1 ? (plotLeft + plotRight) / 2 : x(dates.indexOf(date)));
  const y = linearScale(yMin, yMax, plotBottom, plotTop);
  const formatPoint = settings.formatValue || ((value) => formatValue(value, unit));
  const formatX = settings.formatDate || formatDate;
  const built = list.map((item, index) => {
    const byDate = {};
    item.points.forEach((point) => {
      if (point && point.date) byDate[point.date] = point;
    });
    const coords = dates.map((date) => {
      const point = byDate[date];
      if (!point || !isNumber(point.value)) return null;
      return { x: xOf(date), y: y(point.value), date: date, value: point.value, partial: point.partial === true };
    });
    const dots = coords.filter(Boolean).map((point) => ({
      x: point.x,
      y: point.y,
      partial: point.partial,
      title: item.label + ' · ' + formatX(point.date) + ': ' + formatPoint(point.value) + (point.partial ? ' (parcial)' : ''),
    }));
    const lastDot = dots.length ? dots[dots.length - 1] : null;
    const lastPoint = coords.filter(Boolean).pop();
    return {
      key: item.key || String(index),
      label: item.label || '',
      color: item.color || SERIES_COLORS[index % SERIES_COLORS.length],
      dash: item.dash !== undefined ? item.dash : SERIES_DASHES[index % SERIES_DASHES.length],
      path: linePath(coords),
      area: item.area ? areaPath(coords, plotBottom) : '',
      dots: dots,
      points: coords,
      window: item.window || 1,
      higherIsBetter: item.higherIsBetter !== false,
      last: lastDot,
      lastText: lastPoint ? formatPoint(lastPoint.value) : '',
    };
  });
  return {
    width: width,
    height: height,
    empty: empty,
    plot: { left: plotLeft, right: plotRight, top: plotTop, bottom: plotBottom },
    dates: dates,
    columns: dates.map((date) => ({ date: date, x: xOf(date) })),
    yTicks: ticks.map((value) => ({ value: value, y: y(value), label: formatPoint(value) })),
    xTicks: labelIndexes(dates.length, settings.xLabelCount || 6).map((index) => ({
      x: xOf(dates[index]),
      label: formatX(dates[index]),
    })),
    series: built,
  };
};

export const MIN_CHART_WIDTH = 280;

export const DEFAULT_CHART_WIDTH = 900;

export const responsiveChartSize = (measuredWidth, wantedLabels) => {
  const measured = isNumber(measuredWidth) && measuredWidth > 0 ? Math.floor(measuredWidth) : DEFAULT_CHART_WIDTH;
  const width = Math.max(MIN_CHART_WIDTH, measured);
  const wanted = isNumber(wantedLabels) && wantedLabels > 1 ? Math.floor(wantedLabels) : 6;
  return { width: width, xLabelCount: Math.max(2, Math.min(wanted, Math.floor(width / 90))) };
};

export const sparklineGeometry = (points, width, height) => {
  const w = width || 120;
  const h = height || 32;
  const pad = 2;
  const values = (points || []).map((point) => (point && isNumber(point.value) ? point.value : null));
  const range = extent(values);
  if (range.max === null) return { empty: true, path: '', area: '', last: null, min: null, max: null, first: null, lastValue: null };
  const x = linearScale(0, Math.max(1, values.length - 1), pad, w - pad);
  const y = linearScale(range.min, range.max === range.min ? range.min + 1 : range.max, h - pad, pad);
  const coords = values.map((value, index) => (value === null ? null : {
    x: values.length === 1 ? w / 2 : x(index),
    y: range.max === range.min ? h / 2 : y(value),
  }));
  const valid = coords.filter(Boolean);
  const numeric = values.filter((value) => value !== null);
  return {
    empty: false,
    path: linePath(coords),
    area: areaPath(coords, h - pad),
    last: valid[valid.length - 1],
    min: range.min,
    max: range.max,
    first: numeric[0],
    lastValue: numeric[numeric.length - 1],
  };
};

export const normalizeBars = (rows, valueKey, maxValue) => {
  const key = valueKey || 'value';
  const list = rows || [];
  const values = list.map((row) => (row && isNumber(row[key]) ? row[key] : 0));
  const max = isNumber(maxValue) && maxValue > 0 ? maxValue : Math.max.apply(null, [0].concat(values));
  return list.map((row, index) => {
    const value = values[index];
    const share = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
    return Object.assign({}, row, { widthPct: value > 0 ? Math.max(share, 1.5) : 0 });
  });
};

const OFFSET_PREFIX = { day: 'D', week: 'W', month: 'M' };

export const offsetLabel = (offset, granularity) => (OFFSET_PREFIX[granularity] || 'D') + offset;

export const cohortLabel = (cohortKey) => {
  const key = cohortKey === null || cohortKey === undefined ? '' : String(cohortKey);
  if (/^\d{4}-\d{2}$/.test(key)) return formatMonth(key);
  if (/^\d{4}-\d{2}-\d{2}/.test(key)) return formatDateFull(key);
  return key || '—';
};

const cohortCell = (cell, offset, size) => {
  if (!cell) return { offset: offset, text: '—', title: 'Sem célula para este período.', pending: false, missing: true, style: null };
  if (cell.matured !== true) {
    return {
      offset: offset,
      text: immatureText(cell.maturityDate),
      title: 'Coorte imatura: o valor sai em ' + formatDateFull(cell.maturityDate) + '.',
      pending: true,
      missing: false,
      style: null,
    };
  }
  if (!isNumber(cell.pct)) {
    return { offset: offset, text: 'n/d', title: 'Sem valor para esta célula.', pending: false, missing: true, style: null };
  }
  const style = sequentialStyle(cell.pct);
  const parts = [];
  if (isNumber(cell.retainedCumulative)) {
    parts.push('Retidos acumulados: ' + formatInteger(cell.retainedCumulative) + (isNumber(size) ? ' de ' + formatInteger(size) : ''));
  }
  if (isNumber(cell.retainedExact)) parts.push('ativos exatamente neste período: ' + formatInteger(cell.retainedExact));
  return {
    offset: offset,
    text: formatPercent(cell.pct),
    title: formatPercent(cell.pct) + (parts.length ? ' · ' + parts.join(' · ') : ''),
    pending: false,
    missing: false,
    style: { background: style.background, color: style.color },
  };
};

export const cohortRows = (cohorts, offsets) => (cohorts || []).filter(Boolean).map((cohort) => {
  const byOffset = {};
  (cohort.cells || []).forEach((cell) => {
    if (cell && isNumber(cell.offset)) byOffset[cell.offset] = cell;
  });
  return {
    key: String(cohort.cohortKey),
    label: cohortLabel(cohort.cohortKey),
    size: isNumber(cohort.size) ? cohort.size : null,
    sizeText: formatInteger(cohort.size),
    companiesText: isNumber(cohort.companies) ? formatInteger(cohort.companies) + (cohort.companies === 1 ? ' empresa' : ' empresas') : '',
    derived: cohort.source === 'B' || cohort.source === 'D',
    cells: (offsets || []).map((offset) => cohortCell(byOffset[offset], offset, cohort.size)),
  };
});



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piScale.js